import { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Spinner, Tab, Tabs } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import useApi from "../hooks/useApi";
import { AuthContext } from "../context/AuthContext";
import ForgotPasswordModal from "./ForgotPasswordModal";

const AgentAuthModal = ({ show, onClose, isRegister = false }) => {
  const navigate = useNavigate();
  const { publicAuth } = useApi();
  const { loginUser, saveAuthToken, setViewMode } = useContext(AuthContext);

  const [isLoginView, setIsLoginView] = useState(!isRegister);
  const [loginMethod, setLoginMethod] = useState("email");

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  const [name, setName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regShowPassword, setRegShowPassword] = useState(false);
  const [regErrors, setRegErrors] = useState({});
  const [isRegLoading, setIsRegLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [cities, setCities] = useState([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [regCityIds, setRegCityIds] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [isOtpView, setIsOtpView] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  useEffect(() => {
    if (show) {
      setIsLoginView(!isRegister);
    }
  }, [show, isRegister]);

  useEffect(() => {
    if (show && !isLoginView && !isOtpView) {
      fetchCities();
    }
  }, [show, isLoginView, isOtpView]);

  const resetFormFields = () => {
    setEmail("");
    setPhone("");
    setPassword("");
    setShowPassword(false);
    setLoginErrors({});
    setIsLoginLoading(false);
    setLoginMethod("email");

    setName("");
    setRegEmail("");
    setRegPhone("");
    setRegPassword("");
    setRegShowPassword(false);
    setRegErrors({});
    setIsRegLoading(false);
    setAgreeTerms(false);
    setRegCityIds([]);
    setCitySearch("");
    setIsOtpView(false);
    setOtp("");
    setIsOtpLoading(false);
    setShowForgot(false);
  };

  const handleClose = () => {
    resetFormFields();
    onClose();
  };

  const handleForgotClick = () => {
    setShowForgot(true);
  };

  const handleForgotClose = () => {
    setShowForgot(false);
  };

  const validateLogin = () => {
    const next = {};
    if (loginMethod === "email") {
      if (!email.trim()) next.email = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        next.email = "Enter a valid email";
      }
    } else {
      if (!phone.trim()) next.phone = "Phone is required";
      else if (!/^\d{10}$/.test(phone)) next.phone = "Enter 10-digit phone number";
    }

    if (!password.trim()) next.password = "Password is required";
    else if (password.length < 6) next.password = "Minimum 6 characters";

    setLoginErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateRegister = () => {
    const next = {};
    if (!name.trim()) next.name = "Full name is required";
    if (!regEmail.trim()) next.regEmail = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail)) {
      next.regEmail = "Enter a valid email";
    }
    if (!regPhone.trim()) next.regPhone = "Phone is required";
    else if (!/^\d{10}$/.test(regPhone)) next.regPhone = "Enter 10-digit phone number";
    if (!regPassword.trim()) next.regPassword = "Password is required";
    else if (regPassword.length < 6) next.regPassword = "Minimum 6 characters";
    if (!regCityIds || regCityIds.length === 0) next.regCityIds = "Please select at least one city";
    if (!agreeTerms) next.agreeTerms = "You must agree to the terms";

    setRegErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    setIsLoginLoading(true);
    try {
      const payload = {
        [loginMethod === "email" ? "email" : "phone"]:
          loginMethod === "email" ? email : phone,
        password,
      };

      const response = await publicAuth("agent/login", "POST", payload);
      if (response.data?.status === true) {
        const agent = response.data?.data || null;
        const approved =
          agent?.is_approved ?? agent?.approved ?? agent?.isApproved ?? null;

        if (approved === false || approved === 0 || approved === "0") {
          Swal.fire({
            icon: "info",
            title: "Pending Approval",
            text: "Your registration is under Asysta team review. Please try again shortly.",
          });
          return;
        }

        loginUser(agent);
        setViewMode("agent");
        saveAuthToken(response.data?.token);
        navigate("/agent/dashboard");
        handleClose();
      } else {
        throw new Error(response.data?.message || "Invalid credentials");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error.response?.data?.message || error.message || "Something went wrong.",
      });
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateRegister()) return;

    setIsRegLoading(true);
    try {
      const payload = {
        name,
        email: regEmail,
        phone: regPhone,
        password: regPassword,
        city_ids: regCityIds,
      };

      const response = await publicAuth("agent/register", "POST", payload);
        if (response.data?.status === true) {
          Swal.fire({
            icon: "success",
            title: "Registration Submitted",
            text: "OTP sent to your email. Please verify to continue.",
          });
          setIsOtpView(true);
        } else {
        throw new Error(response.data?.message || "Registration failed");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: error.response?.data?.message || error.message || "Something went wrong.",
      });
    } finally {
      setIsRegLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.trim().length !== 6) {
      Swal.fire({
        icon: "error",
        title: "Invalid OTP",
        text: "Please enter a valid 6-digit OTP.",
      });
      return;
    }

    setIsOtpLoading(true);
    try {
      const response = await publicAuth("agent/verify-otp", "POST", {
        email: regEmail,
        otp: otp.trim(),
      });
      if (response.data?.status === true) {
        Swal.fire({
          icon: "success",
          title: "Verified",
          text: "Email verified. Please wait for Asysta team review before login.",
        });
        setIsOtpView(false);
        setIsLoginView(true);
      } else {
        throw new Error(response.data?.message || "OTP verification failed");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "OTP Failed",
        text: error.response?.data?.message || error.message || "Something went wrong.",
      });
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!regEmail) {
      Swal.fire({
        icon: "error",
        title: "Missing Email",
        text: "Please enter your email again.",
      });
      return;
    }

    try {
      const response = await publicAuth("agent/resend-otp", "POST", {
        email: regEmail,
      });
      if (response.data?.status === true) {
        Swal.fire({
          icon: "success",
          title: "OTP Sent",
          text: "A new OTP has been sent to your email.",
        });
      } else {
        throw new Error(response.data?.message || "Failed to resend OTP");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Resend Failed",
        text: error.response?.data?.message || error.message || "Something went wrong.",
      });
    }
  };

  const fetchCities = async () => {
    if (cities.length > 0) return;
    setIsCitiesLoading(true);
    try {
      const response = await publicAuth("cities");
      if (response.data?.status && Array.isArray(response.data.data)) {
        setCities(response.data.data);
      } else {
        throw new Error(response.data?.message || "Failed to load cities");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message || "Failed to load cities.",
      });
    } finally {
      setIsCitiesLoading(false);
    }
  };

  const filteredCities = cities.filter((c) =>
    String(c?.name || "")
      .toLowerCase()
      .includes(citySearch.trim().toLowerCase())
  );

  if (!show) return null;

  return (
    <>
      <ForgotPasswordModal
        show={showForgot}
        handleClose={handleForgotClose}
        userType="agent"
      />

      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="md"
        style={showForgot ? { pointerEvents: "none", opacity: 0.5 } : {}}
      >
      <Modal.Header closeButton>
        <Modal.Title className="w-100 text-center">
          {isLoginView ? "Agent Login" : isOtpView ? "Agent OTP Verification" : "Agent Registration"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={showForgot ? { pointerEvents: "none" } : {}}>
        {!isLoginView && !isOtpView && (
          <div className="alert alert-info py-2 small">
            Registration requires Asysta team review before you can login.
          </div>
        )}

        {isLoginView ? (
          <>
            <Tabs
              activeKey={loginMethod}
              onSelect={(k) => setLoginMethod(k)}
              className="mb-3 justify-content-center"
            >
              <Tab eventKey="email" title="Email Login" />
              <Tab eventKey="phone" title="Phone Login" />
            </Tabs>

            <Form onSubmit={handleLogin}>
              {loginMethod === "email" ? (
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setLoginErrors((prev) => ({ ...prev, email: "" }));
                    }}
                    isInvalid={!!loginErrors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {loginErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                      setLoginErrors((prev) => ({ ...prev, phone: "" }));
                    }}
                    isInvalid={!!loginErrors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {loginErrors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setLoginErrors((prev) => ({ ...prev, password: "" }));
                    }}
                    isInvalid={!!loginErrors.password}
                  />
                  <Button
                    variant="link"
                    className="position-absolute end-0 top-0 text-decoration-none"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {loginErrors.password}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>

              <div className="d-grid mb-3">
                <Button variant="primary" type="submit" disabled={isLoginLoading}>
                  {isLoginLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>
              </div>

              <div className="text-center">
                <Button variant="link" className="p-0" onClick={() => setIsLoginView(false)}>
                  Register as Agent
                </Button>
                <Button
                  variant="link"
                  className="p-0 ms-3 text-decoration-none"
                  onClick={handleForgotClick}
                  disabled={showForgot}
                >
                  Forgot Password?
                </Button>
              </div>
            </Form>
          </>
        ) : isOtpView ? (
          <Form onSubmit={handleVerifyOtp}>
            <Form.Group className="mb-3">
              <Form.Label>Enter OTP</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                placeholder="6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </Form.Group>

            <div className="d-grid mb-3">
              <Button variant="primary" type="submit" disabled={isOtpLoading}>
                {isOtpLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </Button>
            </div>

            <div className="text-center">
              <Button variant="link" className="p-0 me-3" onClick={handleResendOtp}>
                Resend OTP
              </Button>
              <Button variant="link" className="p-0" onClick={() => { setIsOtpView(false); setIsLoginView(true); }}>
                Back to Login
              </Button>
            </div>
          </Form>
        ) : (
          <Form onSubmit={handleRegister}>
            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter full name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setRegErrors((prev) => ({ ...prev, name: "" }));
                }}
                isInvalid={!!regErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {regErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={regEmail}
                onChange={(e) => {
                  setRegEmail(e.target.value);
                  setRegErrors((prev) => ({ ...prev, regEmail: "" }));
                }}
                isInvalid={!!regErrors.regEmail}
              />
              <Form.Control.Feedback type="invalid">
                {regErrors.regEmail}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                value={regPhone}
                onChange={(e) => {
                  setRegPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                  setRegErrors((prev) => ({ ...prev, regPhone: "" }));
                }}
                isInvalid={!!regErrors.regPhone}
              />
              <Form.Control.Feedback type="invalid">
                {regErrors.regPhone}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Select Cities <span className="text-danger">*</span>
              </Form.Label>

              <div className="border rounded p-2">
                <Form.Control
                  type="text"
                  placeholder="Search city..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  className="mb-2"
                  disabled={isCitiesLoading}
                />

                <div
                  className="d-flex flex-wrap gap-2"
                  style={{ maxHeight: "180px", overflowY: "auto" }}
                >
                  {(filteredCities.length ? filteredCities : cities).map((city) => {
                    const checked = regCityIds.includes(Number(city.id));
                    return (
                      <label
                        key={city.id}
                        className={`badge rounded-pill px-3 py-2 border ${checked ? "bg-primary text-white" : "bg-light text-dark"}`}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        <input
                          type="checkbox"
                          className="me-2"
                          checked={checked}
                          onChange={() => {
                            const id = Number(city.id);
                            setRegCityIds((prev) => {
                              const next = prev.includes(id)
                                ? prev.filter((x) => x !== id)
                                : [...prev, id];
                              return next;
                            });
                            setRegErrors((prev) => ({ ...prev, regCityIds: "" }));
                          }}
                          style={{ verticalAlign: "middle" }}
                        />
                        {city.name}
                      </label>
                    );
                  })}
                </div>
              </div>

              {isCitiesLoading && (
                <Form.Text className="text-muted">Loading cities...</Form.Text>
              )}
              <Form.Text className="text-muted">
                Click to select multiple cities.
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {regErrors.regCityIds}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={regShowPassword ? "text" : "password"}
                  placeholder="Create password"
                  value={regPassword}
                  onChange={(e) => {
                    setRegPassword(e.target.value);
                    setRegErrors((prev) => ({ ...prev, regPassword: "" }));
                  }}
                  isInvalid={!!regErrors.regPassword}
                />
                <Button
                  variant="link"
                  className="position-absolute end-0 top-0 text-decoration-none"
                  onClick={() => setRegShowPassword(!regShowPassword)}
                >
                  {regShowPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {regErrors.regPassword}
                </Form.Control.Feedback>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                id="agent-agree-terms"
                type="checkbox"
                label="I agree to the Terms and Conditions"
                checked={agreeTerms}
                onChange={(e) => {
                  setAgreeTerms(e.target.checked);
                  setRegErrors((prev) => ({ ...prev, agreeTerms: "" }));
                }}
                isInvalid={!!regErrors.agreeTerms}
              />
              <Form.Control.Feedback type="invalid">
                {regErrors.agreeTerms}
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-grid mb-3">
              <Button variant="primary" type="submit" disabled={isRegLoading}>
                {isRegLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Registering...
                  </>
                ) : (
                  "Register"
                )}
              </Button>
            </div>

            <div className="text-center">
              <Button variant="link" className="p-0" onClick={() => setIsLoginView(true)}>
                Already have an account? Login
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
      </Modal>
    </>
  );
};

export default AgentAuthModal;
